import React, { useReducer, useEffect, useCallback } from "react";
import Modal, { ModalProps } from "antd/lib/modal";
import { IFamilyTreePerson, IImageMemory } from "../../../interfaces";
import { CancelTokenSource, AxiosResponse, AxiosError } from "axios";
import { Store } from "antd/lib/form/interface";
import { InboxOutlined, SyncOutlined } from "@ant-design/icons";
import axios from "axios";
import {
  Spin,
  Upload,
  Form,
  DatePicker,
  Input,
  Select,
  notification,
} from "antd";
import * as _ from "underscore";
import AppTheme from "../../../theme/AppTheme";
import { useForm } from "antd/lib/form/util";
import { Backend } from "../../../helpers";

type AddImageMemoryProps = {
  visible: boolean;
  initialValue?: IImageMemory;
  onSuccess: (image: IImageMemory) => void;
  handleCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
};

export declare type AddImageMemoryState = {
  personSearchResult: IFamilyTreePerson[];
  searchingPerson: boolean;
  modalVisible: boolean;
  loading: boolean;
  searchPersonTS: CancelTokenSource;
  submitImageTS: CancelTokenSource;
  changedValues: Store;
};

const AddImageMemory: React.FC<ModalProps & AddImageMemoryProps> = (props) => {
  /**
   * States management
   */
  const reducer = (
    state: AddImageMemoryState,
    action: {
      type: "updateState" | "startLoading" | "stopLoading";
      state?: Partial<AddImageMemoryState>;
    }
  ): AddImageMemoryState => {
    switch (action.type) {
      case "updateState":
        return {
          modalVisible: action.state?.modalVisible ?? state.modalVisible,
          loading: action.state?.loading ?? state.loading,
          personSearchResult:
            action.state?.personSearchResult ?? state.personSearchResult,
          searchingPerson:
            action.state?.searchingPerson ?? state.searchingPerson,
          searchPersonTS: action.state?.searchPersonTS ?? state.searchPersonTS,
          submitImageTS: action.state?.submitImageTS ?? state.submitImageTS,
          changedValues: action.state?.changedValues ?? state.changedValues,
        };
      case "startLoading": {
        const { loading, ...rest } = state;
        return {
          loading: true,
          ...rest,
        };
      }
      case "stopLoading": {
        const { loading, ...rest } = state;
        return {
          loading: false,
          ...rest,
        };
      }
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    modalVisible: props.visible,
    loading: false,
    personSearchResult: [],
    searchingPerson: false,
    searchPersonTS: axios.CancelToken.source(),
    submitImageTS: axios.CancelToken.source(),
    changedValues: {},
  });

  /**
   * Update modal state
   */
  useEffect(() => {
    dispatch({ type: "updateState", state: { modalVisible: props.visible } });
  }, [props.visible]);

  /**
   * Cancel pending token
   */
  useEffect(() => {
    return () => {
      state.searchPersonTS.cancel();
      state.submitImageTS.cancel();
    };
  }, [state.searchPersonTS, state.submitImageTS]);

  const [form] = useForm();

  /**
   * Load created persons
   */
  const loadPersons = useCallback(async () => {
    // start loading
    dispatch({ type: "startLoading" });
    // fetch persons
    await Backend.getInstance()
      .axios()
      .get("/api/createnode/", {
        cancelToken: state.searchPersonTS.token,
      })
      .then((response: AxiosResponse<IFamilyTreePerson[]>) => {
        dispatch({
          type: "updateState",
          state: {
            personSearchResult: response.data,
          },
        });
      })
      .catch((error: AxiosError<any>) => {
        if (!axios.isCancel(error)) {
          notification.warn({
            message: "Failed to load persons you have created.",
          });
        }
      })
      .finally(() => {
        dispatch({ type: "stopLoading" });
      });
  }, [state.searchPersonTS.token]);

  /**
   * Load persons when the component is mounted
   */
  useEffect(() => {
    loadPersons();
  }, [loadPersons]);

  /**
   * Create an image
   */
  const createImage = useCallback(
    async (values: Store) => {
      // start loading
      dispatch({ type: "startLoading" });
      // submit images
      await Backend.getInstance()
        .axios()
        .post(
          "/memories/images/",
          {
            image: values.image,
            persons: _.pluck(values.persons, "value"),
            topic_tags: values.topic_tags,
            memory_date: values.memory_date.format("YYYY-MM-DD"),
            memory_place: values.memory_place,
            description: values.description,
          },
          {
            cancelToken: state.submitImageTS.token,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        )
        .then((response: AxiosResponse<IImageMemory>) => {
          notification.success({
            message: "The image has been successfully created.",
          });
          props.onSuccess(response.data);
        })
        .catch((error: AxiosError<any>) => {
          if (!axios.isCancel(error)) {
            notification.error({
              message: "Failed to created image. Try again later please.",
            });
          }
        })
        .finally(() => {
          // stop loading
          dispatch({ type: "stopLoading" });
        });
    },
    [props, state.submitImageTS.token]
  );

  const onFinish = (values: Store) => {
    console.log("Add memory image form values", values);
    createImage(values);
  };

  const normFile = (e: any) => {
    console.log("Upload event:", e);
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const valuesChanged = (changedValues: Store, values: Store) => {
    // update state
    dispatch({ type: "updateState", state: { changedValues: changedValues } });
    console.log("Add Image Memory values changed", values);
    // /**
    //  * Search person in database if watched keys changed
    //  */
    // if (
    //   containsKeys(changedValues, ["first_name", "last_name", "birthplace"])
    // ) {
    //   // search potential existing person
    //   searchPerson(values);
    // }
  };

  return (
    <Modal
      maskClosable={false}
      width="740px"
      visible={state.modalVisible}
      title="Add photo"
      onOk={(e) => {
        form.submit();
      }}
      onCancel={props.handleCancel}
      centered={true}
      bodyStyle={{
        borderTop: "0px",
        backgroundColor: AppTheme.colors.modalBackground,
      }}
    >
      <Spin spinning={state.modalVisible && state.loading} size="large">
        <Form
          className="ant-form-extend"
          layout="vertical"
          initialValues={{ layout: "vertical", ...props.initialValue }}
          onValuesChange={valuesChanged}
          scrollToFirstError={true}
          onFinish={onFinish}
          form={form}
        >
          <Form.Item
            name="image"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            label="Image"
            rules={[{ required: true, message: "Image file is required" }]}
          >
            <Upload.Dragger
              multiple={false}
              className="p-4"
              name="image"
              beforeUpload={() => false}
              accept="image/*"
              listType="picture"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag image to this area to upload
              </p>
              <p className="ant-upload-hint">Support for a single.</p>
            </Upload.Dragger>
          </Form.Item>
          <Form.Item name="description" label="Image description">
            <Input.TextArea name="description" />
          </Form.Item>
          <Form.Item
            name="memory_date"
            label="Memory date"
            rules={[
              {
                type: "object",
                required: true,
                message: "Please select date!",
              },
            ]}
          >
            <DatePicker className="w-full" name="memory_date" />
          </Form.Item>
          <Form.Item
            name="memory_place"
            label="Memory place"
            rules={[
              {
                required: true,
                message: "Memory place is required!",
              },
            ]}
          >
            <Input name="memory_place" />
          </Form.Item>
          <Form.Item name="topic_tags" label="Topic tags">
            <Select
              mode="tags"
              className="w-full"
              placeholder="Add tags here"
            ></Select>
          </Form.Item>
          <Form.Item name="persons" label="Tagged persons">
            <Select
              mode="multiple"
              optionFilterProp="children"
              placeholder="Select persons"
              notFoundContent={
                state.searchingPerson ? <Spin size="small" /> : null
              }
              options={state.personSearchResult.map((item) => {
                return {
                  value: item.id,
                  label: [
                    item.first_name,
                    item.middle_name,
                    item.last_name,
                  ].join(" "),
                };
              })}
              className="w-full"
              suffixIcon={<SyncOutlined spin />}
            >
              {state.personSearchResult.map((d) => (
                <Select.Option
                  key={d.id}
                  value={d.id}
                >{`${d.last_name} ${d.middle_name} ${d.first_name}`}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default AddImageMemory;
